import React, { useState } from 'react';
import { Grid, IconButton, InputAdornment, Typography } from '@material-ui/core';
import { useLocale, useAssetStatus } from 'hooks';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import { AssetStatuses } from 'hooks/useAssets/useAssets.types';
import { DataGrid, DataGridCell, DisplayQr } from 'ui';
import { DataGridColumnConfig } from 'ui/dataGrid/DataGrid.types';
import { ManufacturerIcon, CopyIcon, CopyQrIcon, SearchIcon, RecycleIcon } from 'ui/icons';
import { CheckboxGroupField, GenericField, DateRangeField } from 'form/fields';
import { AppRoute } from 'routing/AppRoute.enum';
import { AutosaveForm } from 'form/autosaveForm/AutosaveForm';

import { useStyles } from './BatteryList.styles';
import { BatteryListProps, ExtendedAsset } from './BatteryList.types';

export const BatteryList = ({ batteryList, onFiltersChange, initialStatuses, loading }: BatteryListProps) => {
  const classes = useStyles();
  const { formatMessage } = useLocale();
  const [batteryId, setBatteryId] = useState<string | null>(null);
  const { push } = useHistory();
  const { getIcon, getLabel } = useAssetStatus();

  const formDefaultValues = {
    search: '',
    status: initialStatuses ?? [],
    date: {
      from: undefined,
      to: undefined,
    },
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const columnsConfig: DataGridColumnConfig<ExtendedAsset>[] = [
    {
      name: 'id',
      labelId: 'batteries_list.grid.easybat_code',
      formatCell: data => {
        return (
          <DataGridCell
            description={data.transactionHash}
            title={
              <div className={classes.iconContainer}>
                <Typography className={classes.title} data-testid="cell-title">
                  {data.id}
                </Typography>
                <IconButton
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCopy(data.id);
                  }}
                  size="small"
                >
                  <CopyIcon className={classes.icon} />
                </IconButton>
                <IconButton
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBatteryId(data.id);
                  }}
                  size="small"
                >
                  <CopyQrIcon className={classes.icon} />
                </IconButton>
              </div>
            }
          />
        );
      },
    },
    {
      name: 'status',
      labelId: 'batteries_list.grid.status',
      formatCell: data => {
        return <DataGridCell description={formatMessage({ id: getLabel(data.status) })} icon={getIcon(data.status)} />;
      },
    },
    {
      name: 'origin',
      labelId: 'batteries_list.grid.origin',
    },
    {
      name: 'manufacturer',
      labelId: 'batteries_list.grid.manufacturer',
      formatCell: data => {
        return (
          <DataGridCell
            title={data.manufacturer?.name ?? ''}
            description={data.transactionHash}
            icon={<ManufacturerIcon />}
          />
        );
      },
    },
    {
      name: 'capacity',
      labelId: 'batteries_list.grid.capacity',
      formatCell: data => formatMessage({ id: 'batteries_list.grid.capacity_value' }, { value: data.capacity }),
    },
    {
      name: 'weight',
      labelId: 'batteries_list.grid.weight',
      formatCell: data => formatMessage({ id: 'batteries_list.grid.weight_value' }, { value: data.weight }),
    },
    {
      name: 'power',
      labelId: 'batteries_list.grid.power',
      formatCell: data => formatMessage({ id: 'batteries_list.grid.power_value' }, { value: data.power }),
    },
    {
      name: 'maxVoltage',
      labelId: 'batteries_list.grid.max_voltage',
      formatCell: data => formatMessage({ id: 'batteries_list.grid.max_voltage_value' }, { value: data.maxVoltage }),
    },
    {
      name: 'date',
      labelId: 'batteries_list.grid.date',
      formatCell: data => {
        return (
          <DataGridCell
            title={data.date ? dayjs(data.date).format('YYYY-MM-DD') : '-'}
            description={data.date ? data.transactionHash : null}
            icon={data.date && data.status === AssetStatuses.INACTIVE && <RecycleIcon />}
          />
        );
      },
    },
    {
      name: 'fee',
      labelId: 'batteries_list.grid.fee',
      formatCell: data => formatMessage({ id: 'issue_battery.form.fee_value' }, { value: data.fee?.toFixed(2) }),
    },
  ];

  return (
    <>
      <AutosaveForm onSubmit={onFiltersChange} defaultValues={formDefaultValues}>
        {() => (
          <Grid container className={classes.filters} spacing={2}>
            <Grid item xs={6}>
              <CheckboxGroupField
                name={'status'}
                items={Object.values(AssetStatuses).map(status => ({
                  label: formatMessage({ id: getLabel(status) }),
                  value: status,
                }))}
              />
            </Grid>
            <Grid item xs={3}>
              <DateRangeField name="date" placeholder={formatMessage({ id: 'batteries_filters.date_placeholder' })} />
            </Grid>

            <Grid item xs={3}>
              <GenericField
                name="search"
                placeholder={formatMessage({ id: 'batteries_filters.search_placeholder' })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        )}
      </AutosaveForm>
      <DataGrid<ExtendedAsset>
        columnsConfig={columnsConfig}
        data={batteryList}
        onClick={row => push(AppRoute.batteriesDetails.replace(':id', row.id))}
        loading={loading}
      />
      {batteryId && <DisplayQr isOpened={!!batteryId} handleClose={() => setBatteryId(null)} value={batteryId} />}
    </>
  );
};
